class FragranceNotesCarousel extends HTMLElement {
  #controller;
  #resetting = false;
  #cards = null;
  #realCount = 0;
  #scrollEndTimeout = 0;
  #lastScrollLeft = -1;
  #settleTries = 0;
  #mq = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    // Fresh controller each time we connect — the element can be relocated in the DOM
    // (e.g. moved into the product details column on mobile), which fires
    // disconnect/connect; a reused aborted controller would drop the listeners.
    this.#controller = new AbortController();
    if (this.#mq.matches) this.#init();
    this.#mq.addEventListener('change', this.#onBreakpointChange, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#destroy();
  }

  #onBreakpointChange = (event) => {
    if (event.matches) this.#init();
    else this.#destroy();
  };

  // scrollLeft that centers a card in the viewport, measured from the DOM via
  // getBoundingClientRect (o mesmo sistema de coordenadas do scroller).
  // NUNCA por aritmética (idx * stride) nem por offsetLeft: full-bleed com
  // margem negativa (margin-left: calc(50% - 50vw) na home) gera strides
  // fracionários E offsetLeft relativo à seção (offsetParent), não ao scroller
  // — o alvo calculado cai alguns px fora do snap-point real e o
  // `scroll-snap mandatory` ANIMA a correção (o "giro reverso" ao cruzar 1↔3).
  #centerScrollFor(card) {
    const scrollerRect = this.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const cardLeftInContent = cardRect.left - scrollerRect.left - this.clientLeft + this.scrollLeft;
    return cardLeftInContent - (this.clientWidth - cardRect.width) / 2;
  }

  #init() {
    if (this.dataset.initialised === 'true') return;

    const realCards = Array.from(this.querySelectorAll(':scope > [class*="__card"]:not([data-clone])'));
    if (realCards.length < 2) return;

    this.#realCount = realCards.length;

    const cloneAndMark = (card) => {
      const clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.clone = 'true';
      return clone;
    };

    realCards.forEach((card) => this.appendChild(cloneAndMark(card)));
    realCards
      .slice()
      .reverse()
      .forEach((card) => this.insertBefore(cloneAndMark(card), this.firstChild));

    this.#cards = Array.from(this.children);
    this.scrollLeft = this.#centerScrollFor(this.#cards[this.#realCount]);

    // Listen to both: scroll (debounced) catches all browsers fast,
    // scrollend triggers settle even faster on supporting browsers.
    this.addEventListener(
      'scroll',
      () => {
        clearTimeout(this.#scrollEndTimeout);
        this.#scrollEndTimeout = setTimeout(this.#onSettle, 60);
      },
      { signal: this.#controller.signal, passive: true }
    );

    if ('onscrollend' in this) {
      this.addEventListener(
        'scrollend',
        () => {
          clearTimeout(this.#scrollEndTimeout);
          this.#onSettle();
        },
        { signal: this.#controller.signal, passive: true }
      );
    }

    this.dataset.initialised = 'true';
  }

  #destroy() {
    this.querySelectorAll(':scope > [data-clone="true"]').forEach((node) => node.remove());
    this.#cards = null;
    this.scrollLeft = 0;
    delete this.dataset.initialised;
  }

  #onSettle = () => {
    const cards = this.#cards;
    if (this.#resetting || !cards || !cards.length) return;

    // Espera o scroll REALMENTE parar antes de teleportar. Momentum + a animação
    // do snap ainda disparam scroll events, e em iOS o debounce pode cair numa
    // pausa da animação — teleportar aí deixaria o salto fora do snap-point e o
    // snap animaria a correção (o "giro" visível). Reagenda enquanto o scrollLeft
    // (arredondado, imune ao jitter sub-pixel) ainda muda, mas COM TETO: o guard
    // anterior era ilimitado e, quando o repouso caía a alguns px do centro no
    // layout full-bleed, reagendava pra sempre — matando o loop infinito.
    const sl = Math.round(this.scrollLeft);
    if (sl !== this.#lastScrollLeft && this.#settleTries < 6) {
      this.#lastScrollLeft = sl;
      this.#settleTries += 1;
      clearTimeout(this.#scrollEndTimeout);
      this.#scrollEndTimeout = setTimeout(this.#onSettle, 60);
      return;
    }
    this.#settleTries = 0;

    // Card mais próximo do centro do viewport, medido em coordenadas de tela
    // (getBoundingClientRect) — espaço correto por construção.
    const scrollerRect = this.getBoundingClientRect();
    const viewportCenter = scrollerRect.left + this.clientLeft + this.clientWidth / 2;
    let idx = 0;
    let best = Infinity;
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - viewportCenter);
      if (distance < best) {
        best = distance;
        idx = i;
      }
    }

    let targetIdx = idx;
    if (idx < this.#realCount) targetIdx = idx + this.#realCount;
    else if (idx >= 2 * this.#realCount) targetIdx = idx - this.#realCount;

    if (targetIdx === idx) return;

    this.#resetting = true;
    // Teleporta pelo DELTA medido entre o clone e o card real equivalente:
    // preserva o offset exato em relação ao snap-point, então o salto cai EM
    // CIMA do ponto e o snap (que fica LIGADO — desligar/religar provoca
    // re-snap animado no iOS) não tem nada para corrigir. Loop invisível.
    const previousBehavior = this.style.scrollBehavior;
    this.style.scrollBehavior = 'auto';
    this.scrollLeft += cards[targetIdx].offsetLeft - cards[idx].offsetLeft;
    this.style.scrollBehavior = previousBehavior;
    queueMicrotask(() => {
      this.#resetting = false;
    });
  };
}

if (!customElements.get('fragrance-notes-carousel')) {
  customElements.define('fragrance-notes-carousel', FragranceNotesCarousel);
}
